import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Phone, Bell, Users,
  Calendar, X, Trash2, Check, Settings2,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import { userSettingsService } from '../services/userSettings';
import { logActivity } from '../utils/activityLog';
import { fmtDate } from '../utils/format';
import { getCurrentUser } from '../utils/roles';

// ─── Constants ───────────────────────────────────────────────────────────────

const CALENDAR_MULTI_USER_ROLES = ['owner', 'admin', 'crm_manager', 'head_of_ret', 'head_of_sales', 'sales_team', 'ret_team'];
const CALENDAR_HEAD_ROLES = ['head_of_ret', 'head_of_sales'];
const CALENDAR_FULL_ACCESS_ROLES = ['owner', 'admin', 'crm_manager'];

const EVENT_TYPES = {
  call:     { label: 'Call',     color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',       dot: 'bg-blue-400',   solid: 'bg-blue-500'   },
  reminder: { label: 'Reminder', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', dot: 'bg-yellow-400', solid: 'bg-yellow-500' },
  meeting:  { label: 'Meeting',  color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', dot: 'bg-purple-400', solid: 'bg-purple-500' },
  other:    { label: 'Other',    color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',        dot: 'bg-gray-400',   solid: 'bg-gray-500'  },
};

const STATUS_STYLES = {
  pending: { badge: 'bg-gray-500/20 text-gray-400 border-gray-500/30',   label: 'Pending' },
  done:    { badge: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Done' },
  missed:  { badge: 'bg-red-500/20 text-red-400 border-red-500/30',       label: 'Missed' },
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_NAMES_FULL  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const HOUR_H  = 64; // px per hour in time grid
const SLOT_H  = HOUR_H / 2; // px per 30-min slot
const SLOTS   = 48; // 24 hours × 2

// Map event type → actual CSS color for the left border stripe
const TYPE_BORDER_COLOR = {
  call:     '#3b82f6',
  reminder: '#eab308',
  meeting:  '#a855f7',
  other:    '#6b7280',
};

// ─── Date Helpers ────────────────────────────────────────────────────────────

function getDaysInMonth(year, month) {
  const firstDay = new Date(year, month, 1);
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1; // Mon=0 … Sun=6

  const days = [];
  for (let i = startDow - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d++) {
    days.push(new Date(year, month, d));
  }
  while (days.length < 42) {
    const next = days[days.length - 1];
    days.push(new Date(next.getFullYear(), next.getMonth(), next.getDate() + 1));
  }
  return days;
}

function getWeekDays(date) {
  const d = new Date(date);
  const dow = d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0
  d.setDate(d.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    return day;
  });
}

// Parse a datetime string from the server as UTC → device local time.
// Server stores naive UTC datetimes (no timezone suffix). We append 'Z' so
// the browser treats the value as UTC and converts to the device's timezone.
function toLocalDate(str) {
  if (!str) return null;
  if (str.endsWith('Z') || /[+\-]\d{2}:\d{2}$/.test(str)) return new Date(str);
  return new Date(str + 'Z');
}

function toMinutes(dateStr) {
  const d = toLocalDate(dateStr);
  if (!d) return 0;
  return d.getHours() * 60 + d.getMinutes();
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate()
  );
}

function isToday(d) {
  return isSameDay(d, new Date());
}

function fmtTime(dateStr) {
  if (!dateStr) return '';
  const d = toLocalDate(dateStr);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Format a Date as local YYYY-MM-DD — avoids UTC offset shifting the date
function localDateStr(d) {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dy}`;
}

function fmtMonthYear(year, month) {
  return `${MONTH_NAMES[month]} ${year}`;
}

function fmtWeekRange(weekDays) {
  const first = weekDays[0];
  const last  = weekDays[6];
  if (first.getMonth() === last.getMonth()) {
    return `${MONTH_NAMES[first.getMonth()]} ${first.getDate()}–${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${MONTH_NAMES[first.getMonth()]} ${first.getDate()} – ${MONTH_NAMES[last.getMonth()]} ${last.getDate()}, ${last.getFullYear()}`;
}

function fmtFullDay(date) {
  return `${DAY_NAMES_FULL[date.getDay() === 0 ? 6 : date.getDay() - 1]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// ─── Default form ─────────────────────────────────────────────────────────────

const defaultForm = () => ({
  title: '',
  type: 'call',
  description: '',
  start_date: localDateStr(new Date()),
  start_time: '10:00',
  end_time: '',
  all_day: false,
  client_name: '',
  assigned_to: '',
  assigned_to_id: '',
  status: 'pending',
});

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CalendarPage() {
  const { theme } = useTheme();
  const { t, language } = useLanguage();
  const isDark = theme === 'dark';
  const locale = language === 'ru' ? 'ru-RU' : undefined;

  const currentUser = getCurrentUser();
  const canViewMultipleCalendars = CALENDAR_MULTI_USER_ROLES.includes(currentUser.role);
  const shouldShowTeamInEvents = CALENDAR_HEAD_ROLES.includes(currentUser.role) || CALENDAR_FULL_ACCESS_ROLES.includes(currentUser.role);

  // View & navigation
  const [view, setView] = useState('month'); // 'month' | 'week' | 'day' | 'list'
  const today = new Date();
  const [year, setYear]           = useState(today.getFullYear());
  const [month, setMonth]         = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(today);

  // Working hours
  const [workHours, setWorkHours] = useState({ start: 10, end: 19 });
  const [showWorkSettings, setShowWorkSettings] = useState(false);
  const [tempWorkHours, setTempWorkHours] = useState(workHours);

  useEffect(() => {
    let cancelled = false;
    userSettingsService.get('calendar.workHours', { start: 10, end: 19 }).then(value => {
      if (!cancelled && value?.start !== undefined && value?.end !== undefined) {
        setWorkHours(value);
        setTempWorkHours(value);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const saveWorkHours = (wh) => {
    setWorkHours(wh);
    userSettingsService.set('calendar.workHours', wh).catch(() => {});
  };

  // Events
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);

  // Agent filter (managers only)
  const [agents, setAgents]           = useState([]);
  const [agentFilter, setAgentFilter] = useState('');

  // Modal
  const [showModal, setShowModal]       = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm]                 = useState(defaultForm());
  const [saving, setSaving]             = useState(false);
  const [deleting, setDeleting]         = useState(false);

  // Scroll refs for time grid
  const weekScrollRef = useRef(null);
  const dayScrollRef  = useRef(null);

  // ── Load agents (managers only) ──────────────────────────────────────────
  useEffect(() => {
    if (!canViewMultipleCalendars) return;
    api.get('/calendar/assignees')
      .then(({ data }) => setAgents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [canViewMultipleCalendars]);

  // ── Load events ──────────────────────────────────────────────────────────
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      // endOfDay appends T23:59:59 so the backend `start_at <=` includes all events
      // within the day (backend stores UTC datetimes; a plain date string parses as midnight).
      const endOfDay = (d) => localDateStr(d) + 'T23:59:59';

      let dateFrom, dateTo;
      if (view === 'month') {
        const gridDays = getDaysInMonth(year, month);
        dateFrom = localDateStr(gridDays[0]);
        dateTo   = endOfDay(gridDays[gridDays.length - 1]);
      } else if (view === 'week') {
        const weekDays = getWeekDays(selectedDate);
        dateFrom = localDateStr(weekDays[0]);
        dateTo   = endOfDay(weekDays[6]);
      } else if (view === 'day') {
        dateFrom = localDateStr(selectedDate);
        dateTo   = endOfDay(selectedDate);
      } else {
        // list
        const t0 = new Date();
        const t1 = new Date();
        t1.setDate(t1.getDate() + 90);
        dateFrom = localDateStr(t0);
        dateTo   = localDateStr(t1);
      }
      const params = { date_from: dateFrom, date_to: dateTo };
      if (canViewMultipleCalendars && agentFilter) params.assigned_to_id = agentFilter;
      const { data } = await api.get('/calendar', { params });
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t('calendar.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [view, year, month, selectedDate, agentFilter, canViewMultipleCalendars, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Log activity on mount
  useEffect(() => {
    logActivity('view', 'calendar', null, 'Viewed Calendar');
  }, []);

  // ── Auto-scroll time grid to work start ──────────────────────────────────
  useEffect(() => {
    // Scroll to 30 min before work start so the label is visible
    const scrollTo = workHours.start * HOUR_H - SLOT_H;
    if (view === 'week' && weekScrollRef.current) weekScrollRef.current.scrollTop = scrollTo;
    if (view === 'day'  && dayScrollRef.current)  dayScrollRef.current.scrollTop  = scrollTo;
  }, [view, workHours.start]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setSelectedDate(new Date());
  };

  const prevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };
  const nextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };

  const prevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };
  const nextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  };

  // Navigation dispatch by view
  const handlePrev = () => {
    if (view === 'month') prevMonth();
    else if (view === 'week') prevWeek();
    else if (view === 'day') prevDay();
  };
  const handleNext = () => {
    if (view === 'month') nextMonth();
    else if (view === 'week') nextWeek();
    else if (view === 'day') nextDay();
  };

  // Current period label
  const monthName = (monthIndex) => t(`calendar.months.${monthIndex}`);
  const dayShortName = (date) => t(`calendar.daysShort.${date.getDay() === 0 ? 6 : date.getDay() - 1}`);
  const dayFullName = (date) => t(`calendar.daysFull.${date.getDay() === 0 ? 6 : date.getDay() - 1}`);
  const eventTypeLabel = (type) => t(`calendar.types.${type}`);
  const eventStatusLabel = (status) => t(`calendar.statuses.${status}`);
  const eventOwnerParts = (evt) => {
    if (!canViewMultipleCalendars) return [];
    const parts = [];
    if (CALENDAR_FULL_ACCESS_ROLES.includes(currentUser.role) && evt.assignedToDesk) parts.push(evt.assignedToDesk);
    if (shouldShowTeamInEvents && evt.assignedToTeam) parts.push(evt.assignedToTeam);
    if (evt.assignedTo) parts.push(evt.assignedTo);
    return parts.filter(Boolean);
  };
  const eventDisplayTitle = (evt) => {
    const owner = eventOwnerParts(evt).join(' / ');
    return owner ? `${owner}: ${evt.title}` : evt.title;
  };
  const assigneeOptionLabel = (user) => {
    const parts = [];
    if (CALENDAR_FULL_ACCESS_ROLES.includes(currentUser.role) && user.desk) parts.push(user.desk);
    if (shouldShowTeamInEvents && user.team) parts.push(user.team);
    parts.push(user.name);
    return parts.filter(Boolean).join(' / ');
  };

  const periodLabel = () => {
    if (view === 'month') return `${monthName(month)} ${year}`;
    if (view === 'week') {
      const weekDays = getWeekDays(selectedDate);
      const first = weekDays[0];
      const last = weekDays[6];
      if (first.getMonth() === last.getMonth()) {
        return `${monthName(first.getMonth())} ${first.getDate()}-${last.getDate()}, ${first.getFullYear()}`;
      }
      return `${monthName(first.getMonth())} ${first.getDate()} - ${monthName(last.getMonth())} ${last.getDate()}, ${last.getFullYear()}`;
    }
    if (view === 'day') {
      return `${dayFullName(selectedDate)}, ${monthName(selectedDate.getMonth())} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`;
    }
    return t('calendar.upcomingEvents');
  };

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const openCreate = (date, hour = null) => {
    const f = defaultForm();
    if (date) f.start_date = localDateStr(date);
    if (hour !== null) f.start_time = `${String(hour).padStart(2, '0')}:00`;
    setEditingEvent(null);
    setForm(f);
    setShowModal(true);
  };

  const openEdit = (evt) => {
    setEditingEvent(evt);
    // toLocalDate converts UTC → device local, so date/time fields show local values
    const localStart = evt.startAt ? toLocalDate(evt.startAt) : new Date();
    const localEnd   = evt.endAt   ? toLocalDate(evt.endAt)   : null;
    const hhmm = (d) => d ? `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` : '';
    setForm({
      title:          evt.title || '',
      type:           evt.type  || 'call',
      description:    evt.description || '',
      start_date:     localDateStr(localStart),
      start_time:     hhmm(localStart) || '10:00',
      end_time:       hhmm(localEnd),
      all_day:        evt.allDay || false,
      client_name:    evt.clientName || '',
      assigned_to:    evt.assignedTo || '',
      assigned_to_id: evt.assignedToId ? String(evt.assignedToId) : '',
      status:         evt.status || 'pending',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setForm(defaultForm());
  };

  // ── Build payload ─────────────────────────────────────────────────────────
  // Convert a local "YYYY-MM-DD" + "HH:MM" to a UTC ISO string for the server.
  // new Date("YYYY-MM-DDTHH:MM:SS") is parsed as LOCAL time in modern browsers,
  // so .toISOString() correctly returns the UTC equivalent.
  const toUTCStr = (dateStr, timeStr) => {
    const local = new Date(`${dateStr}T${timeStr || '00:00'}:00`);
    return local.toISOString().slice(0, 19); // "2026-04-16T07:00:00" (UTC, no Z)
  };

  const buildPayload = () => {
    const f = form;
    let start_at, end_at = null;
    if (f.all_day) {
      // All-day: store midnight UTC of the chosen local date
      start_at = toUTCStr(f.start_date, '00:00');
      end_at   = null;
    } else {
      start_at = toUTCStr(f.start_date, f.start_time || '00:00');
      end_at   = f.end_time ? toUTCStr(f.start_date, f.end_time) : null;
    }
    const payload = {
      title:       f.title,
      type:        f.type,
      description: f.description,
      start_at,
      end_at,
      all_day:     f.all_day,
      client_name: f.client_name,
    };
    if (canViewMultipleCalendars) {
      payload.assigned_to    = f.assigned_to;
      payload.assigned_to_id = f.assigned_to_id ? String(f.assigned_to_id) : null;
    }
    return payload;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim()) { toast.error(t('calendar.titleRequired')); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingEvent) {
        await api.put(`/calendar/${editingEvent.id}`, payload);
        if (form.status !== editingEvent.status) {
          await api.patch(`/calendar/${editingEvent.id}/status`, { status: form.status });
        }
        toast.success(t('calendar.eventUpdated'));
      } else {
        await api.post('/calendar', payload);
        toast.success(t('calendar.eventCreated'));
      }
      closeModal();
      loadEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('calendar.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!editingEvent) return;
    setDeleting(true);
    try {
      await api.delete(`/calendar/${editingEvent.id}`);
      toast.success(t('calendar.eventDeleted'));
      closeModal();
      loadEvents();
    } catch {
      toast.error(t('calendar.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // ── Quick status ──────────────────────────────────────────────────────────
  const handleQuickStatus = async (evt, status, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/calendar/${evt.id}/status`, { status });
      toast.success(t('calendar.markedAs').replace('{status}', eventStatusLabel(status)));
      loadEvents();
    } catch {
      toast.error(t('calendar.statusUpdateFailed'));
    }
  };

  // ── Grid days ─────────────────────────────────────────────────────────────
  const gridDays = getDaysInMonth(year, month);

  // ── Events per day (month view) ───────────────────────────────────────────
  const eventsForDay = (day) =>
    events.filter(evt => evt.startAt && isSameDay(toLocalDate(evt.startAt), day));

  // ── List view grouping ────────────────────────────────────────────────────
  const groupedEvents = (() => {
    const sorted = [...events].sort((a, b) =>
      new Date(a.startAt || 0) - new Date(b.startAt || 0)
    );
    const groups = {};
    sorted.forEach(evt => {
      if (!evt.startAt) return;
      const d = toLocalDate(evt.startAt);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(evt);
    });
    return groups;
  })();

  const groupLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const diff = Math.round((d - t) / 86400000);
    if (diff === 0) return t('calendar.today');
    if (diff === 1) return t('calendar.tomorrow');
    return d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // ── Time grid helpers ─────────────────────────────────────────────────────
  const eventsForDayGrid = (day) =>
    events.filter(evt => evt.startAt && isSameDay(toLocalDate(evt.startAt), day) && !evt.allDay);

  const getEventGridStyle = (evt) => {
    const startMin = toMinutes(evt.startAt);
    let endMin = evt.endAt ? toMinutes(evt.endAt) : startMin + 60;
    if (endMin <= startMin) endMin = startMin + 30;
    const top    = (startMin / 60) * HOUR_H;
    const height = Math.max(((endMin - startMin) / 60) * HOUR_H, 28);
    return { top, height };
  };

  // ── Tailwind helpers ──────────────────────────────────────────────────────
  const cls = {
    page:       `min-h-screen ${isDark ? 'bg-[#070b14] text-white' : 'bg-gray-50 text-gray-900'}`,
    card:       `rounded-xl border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input:      `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    label:      `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    btn:        `px-3 py-1.5 text-sm border rounded-lg transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#1a1f35]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50',
    btnDanger:  'px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50',
    select:     `px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    gridBorder: isDark ? 'border-gray-800' : 'border-gray-200',
    workBg:     isDark ? 'bg-[#0f1729]' : 'bg-white',
    nonWorkBg:  isDark ? 'bg-[#070b14]' : 'bg-gray-50',
    timeGutter: `text-xs text-right pr-3 flex-shrink-0 select-none ${isDark ? 'text-gray-600' : 'text-gray-400'}`,
  };

  // ── Time grid column click → openCreate (snaps to 30-min) ───────────────
  const handleGridClick = (e, day) => {
    if (e.target !== e.currentTarget) return;
    const slot   = Math.floor(e.nativeEvent.offsetY / SLOT_H);   // 0-47
    const hour   = Math.floor(slot / 2);
    const minute = (slot % 2) * 30;
    const h = Math.max(0, Math.min(23, hour));
    const m = minute === 30 ? '30' : '00';
    const f = defaultForm();
    f.start_date = localDateStr(day);
    f.start_time = `${String(h).padStart(2, '0')}:${m}`;
    setEditingEvent(null);
    setForm(f);
    setShowModal(true);
  };

  // ── Render time grid (shared between week and day) ────────────────────────
  // Uses 30-minute slots: SLOT_H = 32px, 48 slots total.
  // Full-hour rows have a solid border + label; half-hour rows have a lighter dashed separator.
  const renderTimeGrid = (days, scrollRef) => (
    <div className={`overflow-hidden rounded-xl border ${cls.gridBorder}`}>

      {/* Sticky day-name header */}
      <div
        className={`flex border-b ${cls.gridBorder} sticky top-0 z-10 ${isDark ? 'bg-[#0f1729]' : 'bg-white'}`}
        style={{ paddingLeft: 64 }}
      >
        {days.map((day, i) => {
          const todayFlag = isToday(day);
          const dowLabel  = dayShortName(day);
          return (
            <div
              key={i}
              className={`flex-1 py-2 text-center cursor-pointer hover:opacity-80 transition-opacity border-l ${cls.gridBorder}`}
              onClick={() => { setSelectedDate(day); setView('day'); }}
            >
              <div className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {dowLabel}
              </div>
              <div className={`text-sm font-bold mt-0.5 w-7 h-7 flex items-center justify-center rounded-full mx-auto
                ${todayFlag ? 'bg-purple-600 text-white' : isDark ? 'text-gray-200' : 'text-gray-800'}`}
              >
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scrollable body */}
      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        <div className="relative flex">

          {/* ── Time gutter (48 slots) ── */}
          <div style={{ width: 64, flexShrink: 0 }}>
            {Array.from({ length: SLOTS }, (_, s) => {
              const hr     = Math.floor(s / 2);
              const isHalf = s % 2 === 1;
              const inWork = hr >= workHours.start && hr < workHours.end;
              return (
                <div
                  key={s}
                  style={{ height: SLOT_H }}
                  className={`${inWork ? cls.workBg : cls.nonWorkBg} ${
                    isHalf
                      ? '' // no bottom border on half-hour — drawn by column grid
                      : `border-b ${cls.gridBorder}`
                  }`}
                >
                  {/* Label: HH:00 on full hour, tiny HH:30 on half */}
                  <span
                    className={cls.timeGutter}
                    style={{
                      display: 'block',
                      paddingTop: isHalf ? 1 : 4,
                      width: 60,
                      fontSize: isHalf ? 9 : 11,
                      opacity: isHalf ? 0.45 : 1,
                    }}
                  >
                    {String(hr).padStart(2, '0')}:{isHalf ? '30' : '00'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ── Day columns ── */}
          {days.map((day, di) => {
            const dayEvts = eventsForDayGrid(day);
            return (
              <div
                key={di}
                className={`flex-1 relative border-l ${cls.gridBorder}`}
                style={{ minWidth: 0 }}
              >
                {/* 48 slot rows */}
                {Array.from({ length: SLOTS }, (_, s) => {
                  const hr     = Math.floor(s / 2);
                  const isHalf = s % 2 === 1;
                  const inWork = hr >= workHours.start && hr < workHours.end;
                  return (
                    <div
                      key={s}
                      className={`cursor-pointer ${inWork ? cls.workBg : cls.nonWorkBg}`}
                      onClick={(e) => handleGridClick(e, day)}
                      style={{
                        height: SLOT_H,
                        borderBottom: isHalf
                          ? `1px dashed ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`
                          : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
                      }}
                    />
                  );
                })}

                {/* Positioned event pills */}
                {dayEvts.map(evt => {
                  const { top, height } = getEventGridStyle(evt);
                  const typeStyle  = EVENT_TYPES[evt.type] || EVENT_TYPES.other;
                  const borderColor = TYPE_BORDER_COLOR[evt.type] || TYPE_BORDER_COLOR.other;
                  return (
                    <div
                      key={evt.id}
                      onClick={(e) => { e.stopPropagation(); openEdit(evt); }}
                      className={`absolute rounded overflow-hidden cursor-pointer hover:brightness-110 transition-all z-10 ${typeStyle.color}`}
                      style={{
                        top:        top + 1,
                        height:     Math.max(height - 2, SLOT_H - 2),
                        left:       2,
                        right:      2,
                        borderLeft: `3px solid ${borderColor}`,
                      }}
                    >
                      <div className="w-full h-full px-1.5 py-0.5 text-xs overflow-hidden">
                        <div className="font-semibold truncate leading-tight">{eventDisplayTitle(evt)}</div>
                        {height > SLOT_H && (
                          <div className="opacity-70 truncate mt-0.5">
                            {fmtTime(evt.startAt)}{evt.endAt ? ` – ${fmtTime(evt.endAt)}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={cls.page}>
      <div className="p-6 max-w-screen-xl mx-auto space-y-4">

        {/* ── Top bar ── */}
        <div className="flex flex-wrap items-center gap-3">

          {/* View toggle */}
          <div className={`flex rounded-lg border overflow-hidden text-sm ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
            {[
              { key: 'month', label: t('calendar.views.month') },
              { key: 'week',  label: t('calendar.views.week')  },
              { key: 'day',   label: t('calendar.views.day')   },
              { key: 'list',  label: t('calendar.views.list')  },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`px-4 py-1.5 transition-colors ${
                  view === key
                    ? 'bg-purple-600 text-white'
                    : isDark
                      ? 'text-gray-400 hover:bg-[#1a1f35]'
                      : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Prev / Today / Next */}
          {view !== 'list' && (
            <div className="flex items-center gap-1">
              <button onClick={handlePrev} className={cls.btn}><ChevronLeft size={16} /></button>
              <button onClick={goToday}    className={cls.btn}>{t('calendar.today')}</button>
              <button onClick={handleNext} className={cls.btn}><ChevronRight size={16} /></button>
            </div>
          )}

          {/* Period label */}
          {view !== 'list' && (
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {periodLabel()}
            </span>
          )}

          <div className="flex-1" />

          {/* Agent filter (managers only) */}
          {canViewMultipleCalendars && (
            <select
              value={agentFilter}
              onChange={e => setAgentFilter(e.target.value)}
              className={cls.select}
            >
              <option value="">{t('calendar.allAgents')}</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{assigneeOptionLabel(a)}</option>
              ))}
            </select>
          )}

          {/* Work hours settings */}
          <button
            onClick={() => { setTempWorkHours(workHours); setShowWorkSettings(true); }}
            className={cls.btn + ' flex items-center gap-1.5'}
            title={t('calendar.workHoursSettings')}
          >
            <Settings2 size={15} />
          </button>

          {/* Add event */}
          <button
            onClick={() => openCreate(null)}
            className={cls.btnPrimary + ' flex items-center gap-1.5'}
          >
            <Plus size={16} />
            {t('calendar.addEvent')}
          </button>
        </div>

        {/* ── Month View ── */}
        {view === 'month' && (
          <div className={`${cls.card} overflow-hidden`}>
            {/* Day name header */}
            <div className={`grid grid-cols-7 border-b ${cls.gridBorder}`}>
              {Array.from({ length: 7 }, (_, d) => (
                <div
                  key={d}
                  className={`py-2 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  {t(`calendar.daysShort.${d}`)}
                </div>
              ))}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-7">
              {gridDays.map((day, idx) => {
                const inMonth  = day.getMonth() === month;
                const dayEvents = eventsForDay(day);
                const shown    = dayEvents.slice(0, 3);
                const extra    = dayEvents.length - 3;
                const todayCell = isToday(day);

                return (
                  <div
                    key={idx}
                    onClick={() => openCreate(day)}
                    className={`h-28 p-1.5 border-b border-r cursor-pointer transition-colors
                      ${isDark ? 'border-gray-800 hover:bg-[#1a1f35]/50' : 'border-gray-100 hover:bg-gray-50'}
                      ${!inMonth ? (isDark ? 'opacity-30' : 'opacity-40') : ''}
                    `}
                  >
                    {/* Day number */}
                    <div className="flex justify-end mb-1">
                      <span
                        className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
                          ${todayCell
                            ? 'ring-2 ring-purple-500 text-purple-400 font-bold'
                            : isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}
                      >
                        {day.getDate()}
                      </span>
                    </div>

                    {/* Event chips */}
                    <div className="space-y-0.5 overflow-hidden">
                      {shown.map(evt => {
                        const typeStyle = EVENT_TYPES[evt.type] || EVENT_TYPES.other;
                        return (
                          <div
                            key={evt.id}
                            onClick={e => { e.stopPropagation(); openEdit(evt); }}
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs border truncate cursor-pointer hover:opacity-80 transition-opacity ${typeStyle.color}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${typeStyle.dot}`} />
                            <span className="truncate">{eventDisplayTitle(evt)}</span>
                          </div>
                        );
                      })}
                      {extra > 0 && (
                        <div className={`text-xs px-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {t('calendar.more').replace('{count}', extra)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Week View ── */}
        {view === 'week' && renderTimeGrid(getWeekDays(selectedDate), weekScrollRef)}

        {/* ── Day View ── */}
        {view === 'day' && renderTimeGrid([selectedDate], dayScrollRef)}

        {/* ── List View ── */}
        {view === 'list' && (
          <div className="space-y-4">
            {loading && (
              <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('calendar.loading')}
              </div>
            )}
            {!loading && Object.keys(groupedEvents).length === 0 && (
              <div className={`${cls.card} p-12 text-center`}>
                <Calendar size={40} className="mx-auto mb-3 opacity-30" />
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('calendar.noUpcomingEvents')}</p>
              </div>
            )}
            {!loading && Object.entries(groupedEvents).map(([dateKey, dayEvts]) => (
              <div key={dateKey}>
                <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {groupLabel(dateKey)}
                </div>
                <div className={`${cls.card} divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                  {dayEvts.map(evt => {
                    const typeStyle = EVENT_TYPES[evt.type] || EVENT_TYPES.other;
                    const stStyle   = STATUS_STYLES[evt.status] || STATUS_STYLES.pending;
                    return (
                      <div
                        key={evt.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${isDark ? 'hover:bg-[#1a1f35]/40' : 'hover:bg-gray-50'}`}
                        onClick={() => openEdit(evt)}
                      >
                        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded border font-medium ${typeStyle.color}`}>
                          {eventTypeLabel(evt.type)}
                        </span>
                        {!evt.allDay && (
                          <span className={`flex-shrink-0 text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {fmtTime(evt.startAt)}
                            {evt.endAt ? ` – ${fmtTime(evt.endAt)}` : ''}
                          </span>
                        )}
                        {evt.allDay && (
                          <span className={`flex-shrink-0 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('calendar.allDay')}</span>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {eventDisplayTitle(evt)}
                          </p>
                          {evt.clientName && (
                            <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {evt.clientName}
                            </p>
                          )}
                        </div>
                        {evt.assignedTo && !eventOwnerParts(evt).length && (
                          <span className={`hidden sm:block text-xs flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {evt.assignedTo}
                          </span>
                        )}
                        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded border ${stStyle.badge}`}>
                          {eventStatusLabel(evt.status)}
                        </span>
                        {evt.status !== 'done' && (
                          <button
                            onClick={e => handleQuickStatus(evt, 'done', e)}
                            title={t('calendar.markDone')}
                            className="flex-shrink-0 p-1 rounded hover:bg-green-500/20 text-green-400 transition-colors"
                          >
                            <Check size={14} />
                          </button>
                        )}
                        {evt.status !== 'missed' && evt.status !== 'done' && (
                          <button
                            onClick={e => handleQuickStatus(evt, 'missed', e)}
                            title={t('calendar.markMissed')}
                            className="flex-shrink-0 p-1 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create/Edit Modal ── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)' }}
          onClick={closeModal}
        >
          <div
            className={`w-full max-w-lg rounded-xl border shadow-2xl ${isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editingEvent ? t('calendar.editEvent') : t('calendar.newEvent')}
              </h2>
              <button onClick={closeModal} className={`p-1 rounded hover:opacity-70 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Title */}
              <div>
                <label className={cls.label}>{t('calendar.title')} <span className="text-red-400">*</span></label>
                <input
                  className={cls.input}
                  placeholder={t('calendar.eventTitlePlaceholder')}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />
              </div>

              {/* Type */}
              <div>
                <label className={cls.label}>{t('calendar.type')}</label>
                <select
                  className={cls.input}
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                >
                  {Object.entries(EVENT_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{eventTypeLabel(k)}</option>
                  ))}
                </select>
              </div>

              {/* Start Date + Times */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className={cls.label}>{t('calendar.startDate')}</label>
                  <input
                    type="date"
                    className={cls.input}
                    value={form.start_date}
                    onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                {!form.all_day && (
                  <>
                    <div>
                      <label className={cls.label}>{t('calendar.startTime')}</label>
                      <input
                        type="time"
                        className={cls.input}
                        value={form.start_time}
                        onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className={cls.label}>{t('calendar.endTime')}</label>
                      <input
                        type="time"
                        className={cls.input}
                        value={form.end_time}
                        onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* All Day */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="all_day"
                  checked={form.all_day}
                  onChange={e => setForm(f => ({ ...f, all_day: e.target.checked }))}
                  className="w-4 h-4 accent-purple-600"
                />
                <label htmlFor="all_day" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('calendar.allDay')}
                </label>
              </div>

              {/* Client Name */}
              <div>
                <label className={cls.label}>{t('calendar.clientName')}</label>
                <input
                  className={cls.input}
                  placeholder={t('calendar.optional')}
                  value={form.client_name}
                  onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
                />
              </div>

              {/* Assign To (managers only) */}
              {canViewMultipleCalendars && (
                <div>
                  <label className={cls.label}>{t('calendar.assignTo')}</label>
                  <select
                    className={cls.input}
                    value={form.assigned_to_id}
                    onChange={e => {
                      const agent = agents.find(a => String(a.id) === e.target.value);
                      setForm(f => ({
                        ...f,
                        assigned_to_id: e.target.value,
                        assigned_to:    agent ? agent.name : '',
                      }));
                    }}
                  >
                    <option value="">- {t('calendar.unassigned')} -</option>
                    {agents.map(a => (
                      <option key={a.id} value={String(a.id)}>{assigneeOptionLabel(a)}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Description */}
              <div>
                <label className={cls.label}>{t('calendar.description')}</label>
                <textarea
                  rows={3}
                  className={cls.input + ' resize-none'}
                  placeholder={t('calendar.optionalNotes')}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>

              {/* Status (edit only) */}
              {editingEvent && (
                <div>
                  <label className={cls.label}>{t('calendar.status')}</label>
                  <select
                    className={cls.input}
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  >
                    {Object.entries(STATUS_STYLES).map(([k, v]) => (
                      <option key={k} value={k}>{eventStatusLabel(k)}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                {editingEvent && (
                  <button className={cls.btnDanger} onClick={handleDelete} disabled={deleting}>
                    {deleting ? t('calendar.deleting') : <span className="flex items-center gap-1.5"><Trash2 size={14} /> {t('calendar.delete')}</span>}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button className={cls.btn} onClick={closeModal}>{t('calendar.cancel')}</button>
                <button className={cls.btnPrimary} onClick={handleSave} disabled={saving}>
                  {saving ? t('calendar.saving') : t('calendar.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Work Hours Settings Modal ── */}
      {showWorkSettings && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWorkSettings(false)}
        >
          <div
            className={`w-full max-w-xs rounded-xl border shadow-2xl p-6 space-y-5 ${isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-white border-gray-200'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('calendar.workDayHours')}
              </h3>
              <button
                onClick={() => setShowWorkSettings(false)}
                className={`p-1 rounded hover:opacity-70 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <X size={16} />
              </button>
            </div>

            {/* Start */}
            <div>
              <label className={cls.label}>{t('calendar.workDayStart')}</label>
              <select
                className={cls.input}
                value={tempWorkHours.start}
                onChange={e => setTempWorkHours(w => ({ ...w, start: Number(e.target.value) }))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>

            {/* End */}
            <div>
              <label className={cls.label}>{t('calendar.workDayEnd')}</label>
              <select
                className={cls.input}
                value={tempWorkHours.end}
                onChange={e => setTempWorkHours(w => ({ ...w, end: Number(e.target.value) }))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h + 1} value={h + 1}>{String(h + 1).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>

            {/* Save */}
            <button
              className={cls.btnPrimary + ' w-full'}
              onClick={() => {
                if (tempWorkHours.start >= tempWorkHours.end) {
                  toast.error(t('calendar.startBeforeEnd'));
                  return;
                }
                saveWorkHours(tempWorkHours);
                setShowWorkSettings(false);
                toast.success(t('calendar.workHoursSaved'));
              }}
            >
              {t('calendar.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
